/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useEffect, useState } from 'react';
import EngagementScorePill from 'modules/Common/Components/EngagementScroePill';
import PersonalityBulletWidget from 'modules/Common/Components/PersonalityBulletWidget';
import styled from 'styled-components';
import { Typography, Checkbox } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/es/table';
import ContactInfoCard from 'modules/Common/Components/ContactInfoCard';
import { ContactStatusClass } from 'modules/Audience/constants';
import {
  SecondaryColor,
  boxShadow, maxWidth, primaryActiveColor, primaryColor, primaryFont,
  primaryHeadingColor, retina, secondaryHeadingColor, statusBouncedColor, statusUnsubscribedColor, wMBold, wMedium,
} from 'constants/utilsStyles';
import InfiniteScroll from 'react-infinite-scroll-component';
import { openDetailProfile } from 'modules/GlobalActions';
import { getContactDashboard, getContactDetails } from 'modules/Campaigns/Audience/actions';
import classNames from 'classnames';
import SkeletonCustom from 'modules/Common/Skeleton/SkeletonCustom';
import { getQueryListId } from 'utils';
import { SortOrder } from 'antd/lib/table/interface';
import { IContactItem } from '../../interface';
import SmartTable from './SmartTable';
import ContactsTableAddRemove from './ContactsTableAddRemove';

const { Paragraph } = Typography;

interface WrapperProps {
  isDashboard: boolean,
  newContactAdded: boolean,
  addToListMode: boolean,
}

const Wrapper = styled.div<WrapperProps>`
height: 100%;
.ant-table-wrapper {
  padding: 0px 8px 0px 0px;
  .card-info-with-img-skeleton {
    display: flex;
    align-items: center;
    .card-info-skeleton {
      display: flex;
      flex-direction: column;
    }
  }
  .ant-table {
        ::-webkit-scrollbar {
          width: 7px;
        }
        ::-webkit-scrollbar-track {
          background-color: #cfdae8;
          border-radius: 8px;
        }
        ::-webkit-scrollbar-thumb {
          background-color: ${SecondaryColor};
          border-radius: 20px;
          border: 0px solid transparent;
          background-clip: content-box;
        }
    .ant-table-container {
      .ant-table-header {
        position: sticky;
        top: 0px;
        background-color: #fff;
        z-index: 1;
        table {
         border-collapse: separate;
         border-spacing: 8px 8px;
         table-layout: unset !important;
       } 
        .ant-table-thead {
          border-collapse: collapse;
          border-spacing: 0px 0px;
          .ant-table-cell {
            font-family: ${primaryFont};
            font-weight: ${wMedium};
            font-size: 16px;
            line-height: 18px;
            color: ${secondaryHeadingColor};
            background: #fff;
            border-bottom: unset;
            &:first-child {
              padding-left: 22px;
              @media all and (max-width: ${maxWidth}), ${retina} { 
                padding-left: 32px;
              }
            }
            &:nth-child(2) {
              padding-left:${(props) => (props.isDashboard ? '100px' : '86px')};
            }
            /* &:nth-last-child(2) {
              text-align: end;
            } */
            &:last-child {
              display: none;
            }
            &.ant-table-selection-column {
              .ant-table-selection {
                display: ${(props) => props.isDashboard && 'none'};
                .ant-checkbox-wrapper {
                  .ant-checkbox-indeterminate {
                    .ant-checkbox-inner{
                      &::after {
                        background-color: #fff;
                      }
                    }   
                  } 
                }
              }
            }
            & .ant-checkbox-wrapper {
              display: ${(props) => props.isDashboard && 'none'};
            }
          }
        }
      }
      .ant-table-body {
        padding-left: 16px;
        overflow-x: hidden !important;
        /* max-height: ${({ addToListMode }) => (addToListMode ? '270px !important' : '100%')}; */
        ::-webkit-scrollbar {
          width: 7px;
        }
        ::-webkit-scrollbar-track {
          background-color: #cfdae8;
          border-radius: 8px;
        }
        ::-webkit-scrollbar-thumb {
          background-color: ${SecondaryColor};
          border-radius: 20px;
          border: 0px solid transparent;
          background-clip: content-box;
        }
        table {
         border-collapse: separate;
         border-spacing: 0px 8px;
         padding: 0 8px 0px 0px;
         colgroup {
          .ant-table-selection-col {
            width: 45px !important;
          }
         }
       }
        .ant-table-tbody {
          .ant-table-row {
            border-collapse: collapse;
            border-spacing: 0px 0px;
            &:nth-child(2){
              outline: ${(props) => (props.newContactAdded ? `2px solid ${primaryColor}` : 'none')};
            }
            &.ant-table-row-selected {
              background: ${primaryActiveColor};
              outline: 2px solid ${primaryColor};
              .ant-table-cell {
                &:first-child{
                  border-top-left-radius: 13px;
                  border-bottom-left-radius: 13px;
                }
                &:last-child{
                  border-top-right-radius: 13px;
                  border-bottom-right-radius: 13px;
                }
              }
            }
            &:hover {
              cursor: pointer;
            }
            border-radius: 13px;
            box-shadow: ${boxShadow};
            vertical-align: middle;
            .ant-table-cell {
              border-bottom: unset;
              &.ant-table-selection-column {
                padding-left: 25px;
              }
              .contact-tile-company {
                font-family: ${primaryFont};
                font-weight: ${wMedium};
                font-size: 16px;
                line-height: 18px;
                margin-bottom: 0px;
                color: ${primaryHeadingColor};
                .contact-tile-company-div {
                  margin-bottom: 0px;
                }
              }
              .contact-tile-status {
                font-family: ${primaryFont};
                font-weight: ${wMBold};
                font-size: 14px;
                line-height: 16px;
                margin-bottom: 0px;
                color: ${primaryColor};
                &.hard-bounce {
                  color: ${statusBouncedColor};
                }
                &.unsubscribed {
                  color: ${statusUnsubscribedColor};
                }
                & .status-wrapper {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  & .status-wrapper_inner {
                    display: flex;
                  }
                }
              }
              .engagement-score-wrapper {
                justify-content: end;
              }
              &.ant-table-cell-row-hover {
                background: transparent;
                &:first-child{
                  border-top-left-radius: 13px;
                  border-bottom-left-radius: 13px;
                }
                &:last-child{
                  border-top-right-radius: 13px;
                  border-bottom-right-radius: 13px;
                }
              }
            }
          }
        }
      }
    }
  }
  .ant-pagination {
    display: none;
  }
}
`;

interface ISortOrder {
  name: SortOrder,
  company: SortOrder,
  personality: SortOrder,
  status: SortOrder
}
enum TableHeaders {
  NAME = 'full_name',
  CONTACT = 'contact_name',
  COMPANY = 'company_name',
  PERSONALITY = 'personality_name',
  STATUS = 'status',
}

type Props = {
  isDashboard?: boolean,
  isSearched: boolean,
  selectAll: boolean;
  contacts: IContactItem[];
  searchedContacts: IContactItem[];
  totalContacts: number;
  loading?: boolean;
  searchLoading?: boolean;
  totalSearchContacts: number;
  holdSelectedIds: number[];
  updateContactSuccess: boolean;
  isGlobal?: boolean,
  addToListMode?: boolean;
  isListActive?: boolean;
  disableSelectAll?: boolean;
  excludedContactsIds: number[];
  loadMore: () => void,
  handleHoldSelectedIds: (ids: number[], removeExcludedIds?: boolean) => void;
  handleSelectContacts: (contact: IContactItem, type?: string) => void;
  handleSelectAll: (selectAll: boolean) => void;
  handleConfirmation: (type: string, isSingleContact: boolean) => void;
  selectDisable?: boolean;
  newContactAdded: boolean;
  updateSortingOrders: (property: keyof ISortOrder) => void;
  updateSortingConfig: (sortBy: string, sortType: string) => void;
  sortOrder: ISortOrder;
} & typeof defaultProps;

const defaultProps = {
  loading: false,
  isDashboard: false,
  searchLoading: false,
  isGlobal: false,
  addToListMode: false,
  isListActive: false,
  disableSelectAll: false,
  selectDisable: false,
};

interface DataType {
  key: string;
  name: any;
  company: string;
  personality: any;
  status: string;
  // engagement: any;
  contact: IContactItem;
}

interface RecordType {
  name: {
    props: {
      contact: IContactItem
    };
  };
  contact: IContactItem;
}

const ContactsTileTable = function ContactsTileTable(props: Props) {
  // eslint-disable-next-line
  const {
    loading,
    contacts,
    isSearched,
    totalContacts,
    searchedContacts,
    selectAll,
    holdSelectedIds,
    isDashboard,
    searchLoading,
    totalSearchContacts,
    updateContactSuccess,
    isGlobal,
    addToListMode,
    isListActive,
    disableSelectAll,
    excludedContactsIds,
    loadMore,
    handleSelectContacts,
    handleSelectAll,
    handleHoldSelectedIds,
    handleConfirmation,
    selectDisable,
    newContactAdded,
    updateSortingOrders,
    updateSortingConfig,
    sortOrder,
  } = props;

  const hasMore = isSearched ? searchedContacts?.length < totalSearchContacts : contacts?.length < totalContacts;
  const listId = getQueryListId();

  const [tableData, setTableData] = useState<{
    key: any;
    name: JSX.Element;
    company: JSX.Element | any;
    personality: JSX.Element;
    status: any | JSX.Element;
  }[]>([]);

  const handleSetSelectedContacts = (checked: boolean) => {
    handleSelectAll(checked);
    if (checked) {
      if (addToListMode && isGlobal) {
        const selectedContactIds = (isSearched ? searchedContacts : contacts)
          .filter((row) => !row.isListContact)
          .map((rowContact) => rowContact.contactId);
        handleHoldSelectedIds(selectedContactIds);
      } else {
        const ids = (isSearched ? searchedContacts : contacts).map((contact) => contact.contactId);
        handleHoldSelectedIds(ids);
      }
    } else {
      handleHoldSelectedIds([]);
    }
  };

  const checkDisable = (row: IContactItem) => {
    if (addToListMode && isGlobal) {
      return row?.isListContact || isListActive;
    }

    if (addToListMode && !isGlobal) {
      return isListActive;
    }
    return false;
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys: holdSelectedIds,
    onChange: (rowKeys, selectedRows) => {
      // More Params for onChange could be selectedRows, selectionType
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      if (addToListMode && isGlobal) {
        const selectedContactIds = selectedRows
          .filter((row) => !row.contact.isListContact)
          .map((rowContact) => rowContact.contact.contactId);
        handleHoldSelectedIds(selectedContactIds);
      } else {
        handleHoldSelectedIds(rowKeys);
      }

      if (rowKeys.length === 0 && selectAll) {
        handleSetSelectedContacts(false);
      }
    },
    onSelect: (record: RecordType) => {
      // More Params for onselect could be selected, selectedRows 'selected, selectedRows'
      handleSelectContacts(record?.contact);
    },
    onSelectAll: (selected) => {
      // More Params for onSelectAll could be selected, selectedRows 'selectedRows, changeRows'
      // setSelectAll(selected);
      handleSelectAll(selected);
    },
    columnTitle: <Checkbox
      disabled={isListActive || disableSelectAll}
      checked={selectAll && excludedContactsIds.length === 0}
      onChange={(e) => {
        handleSetSelectedContacts(e.target.checked);
      }}
    />,
    getCheckboxProps: (row) => ({
      disabled: selectDisable || checkDisable(row.contact), // Column configuration not to be checked
    }),
  };

  const handleSingleContact = (type: string, contact: IContactItem) => {
    // console.info(contact);
    handleConfirmation(type, true);
    handleSelectContacts(contact, type);
  };

  const createTableData = (loadingData?: boolean) => {
    const ids: number[] = [];
    const newData = [...(isSearched ? searchedContacts : contacts)].map((contact) => {
      if (addToListMode && isGlobal) {
        if (!contact.isListContact) ids.push(contact.contactId);
      } else {
        ids.push(contact.contactId);
      }

      return {
        key: contact.contactId,
        name: <ContactInfoCard contact={contact} />,
        company: <Paragraph className="contact-tile-company-div" ellipsis={{ rows: 1, tooltip: contact.organization }}>{contact.organization}</Paragraph>,
        personality: <PersonalityBulletWidget title={contact.personality} color={contact.color} />,
        status: addToListMode
          ? (
            <div className="status-wrapper">
              {contact.status}
              <div className="status-wrapper_inner">
                <ContactsTableAddRemove
                  isGlobal={isGlobal}
                  clickHandle={(type: string) => handleSingleContact(type, contact)}
                  isListContact={contact.isListContact}
                />
                <EngagementScorePill showSideMenu contactId={contact.contactId} />
              </div>
            </div>
          ) : (
            <div className="status-wrapper">
              <span>{contact.status}</span>
              <EngagementScorePill showSideMenu contactId={contact.contactId} />
            </div>
          ),
        contact,
      };
    });

    if (selectAll) {
      handleHoldSelectedIds(ids, true);
    }

    if (loadingData) {
      console.info('loadingData', loadingData);
      [1, 2, 3, 4].forEach((item) => {
        newData.push({
          key: Math.random() * item,
          name: (
            <div className="card-info-with-img-skeleton">
              <SkeletonCustom
                height="60px"
                width="60px"
                diameter="50%"
                margin="0px 5px 5px 5px"
              />
              <div className="card-info-skeleton">
                <SkeletonCustom
                  height="15px"
                  width="180px"
                  diameter="23px"
                  margin="0px 10px 5px"
                  marginSm="0px 10px 5px"
                />
                {[1, 2].map(() => (
                  <SkeletonCustom
                    height="10px"
                    width="160px"
                    diameter="23px"
                    margin="0px 10px 5px"
                    marginSm="0px 10px 5px"
                  />
                ))}
              </div>
            </div>
          ),
          company: (
            <SkeletonCustom
              height="15px"
              width="100px"
              diameter="23px"
              margin="0px"
            />
          ),
          personality: (
            <SkeletonCustom
              height="15px"
              width="100px"
              diameter="23px"
              margin="0px"
            />
          ),
          status: (
            <SkeletonCustom
              height="15px"
              width="100px"
              diameter="23px"
              margin="0px"
            />
          ),
        });
      });
    }
    setTableData(newData);
  };

  const convertSortOrder = (sortType: string) => {
    if (sortType === null) return 'ASC';
    if (sortType === 'ascend') return 'DESC';
    return '';
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '300px',
      sortOrder: sortOrder.name,
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => {
          updateSortingOrders('name');
          updateSortingConfig(listId ? TableHeaders.CONTACT : TableHeaders.NAME, convertSortOrder(sortOrder.name as string));
        },
      }),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (text) => <p className="contact-tile-company text-truncate">{text}</p>,
      sortOrder: sortOrder.company,
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => {
          updateSortingOrders('company');
          updateSortingConfig(TableHeaders.COMPANY, convertSortOrder(sortOrder.company as string));
        },
      }),
    },
    {
      title: 'Personality',
      dataIndex: 'personality',
      key: 'personality',
      sortOrder: sortOrder.personality,
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => {
          updateSortingOrders('personality');
          updateSortingConfig(TableHeaders.PERSONALITY, convertSortOrder(sortOrder.personality as string));
        },
      }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => <p className={`contact-tile-status text-truncate ${ContactStatusClass[(record?.contact?.status)]}`}>{text}</p>,
      sortOrder: sortOrder.status,
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => {
          updateSortingOrders('status');
          updateSortingConfig(TableHeaders.STATUS, convertSortOrder(sortOrder.status as string));
        },
      }),
    },
  ];

  useEffect(() => {
    createTableData(isSearched ? searchLoading : loading);
  }, [
    contacts,
    searchedContacts,
    loading,
    isSearched,
    totalContacts,
    searchLoading,
    totalSearchContacts,
    updateContactSuccess,
  ]);

  return (
    <Wrapper
      className="contacts-tile-table-wrapper"
      isDashboard={isDashboard}
      newContactAdded={newContactAdded}
      addToListMode={addToListMode}
    >
      <InfiniteScroll
        dataLength={isSearched ? searchedContacts?.length : contacts?.length}
        next={loadMore}
        hasMore={hasMore}
        loader={false}
        height="auto"
        className={classNames('aud-Infinite-scroll contacts-tile-table', {
          emptyState: totalContacts === 0 && searchedContacts?.length === 0 && contacts?.length === 0 && !loading && tableData?.length === 0,
          emptyStateDashboard: isDashboard,
        })}
      >
        <SmartTable
          columns={columns}
          dataSource={tableData}
          rowSelection={{ ...rowSelection }}
          onRow={(record: RecordType, rowIndex: number) => ({
            onClick: (event: MouseEvent) => {
              console.log('rowIndex', rowIndex);
              console.log('event', event);
              openDetailProfile();
              getContactDetails(record?.contact?.contactId?.toString());
              getContactDashboard(record?.contact?.contactId?.toString());
            }, // click row
          })}
          infinity
          onFetch={loadMore}
          hasMore={hasMore}
          isSearched={isSearched}
        />
      </InfiniteScroll>
    </Wrapper>
  );
};

ContactsTileTable.defaultProps = defaultProps;

export default ContactsTileTable;
